import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";

export default function NumberAndAttachmentModal({
  isOpen,
  onClose,
  onSubmit,
  label = "Number",
  placeholder = "",
  defaultValue = "",
  submitText = "Submit",
  allowMultiple = true,
  accept = "image/*,.pdf",
  maxFiles = 10,
  maxFileSizeMB = 25,
  requireFile = true,

  // ✅ NEW
  existingFiles = [],              // [{ name, fileId, type }]
  onRemoveExisting = async () => {}, // async (file) => {}
}) {
  const [value, setValue] = useState(defaultValue ?? "");
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setValue(defaultValue ?? "");
    setFiles([]);
    setError("");
  }, [defaultValue, isOpen]);

  useEffect(() => {
    previews.forEach((p) => p.url && URL.revokeObjectURL(p.url));

    const next = files.map((f) => ({
      name: f.name,
      type: f.type,
      url: f.type?.startsWith("image/") ? URL.createObjectURL(f) : "",
    }));
    setPreviews(next);

    return () => next.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const validateAndAddFiles = (incoming) => {
    const incomingArr = Array.from(incoming || []);
    if (incomingArr.length === 0) return;

    const maxBytes = maxFileSizeMB * 1024 * 1024;

    const filtered = [];
    for (const f of incomingArr) {
      if (f.size > maxBytes) {
        setError(`"${f.name}" is too large (max ${maxFileSizeMB}MB).`);
        continue;
      }
      filtered.push(f);
    }

    setFiles((prev) => {
      const merged = allowMultiple ? [...prev, ...filtered] : filtered.slice(0, 1);
      if (merged.length + (existingFiles?.length || 0) > maxFiles) {
        setError(`You can attach up to ${maxFiles} files (including existing).`);
        return merged.slice(0, Math.max(0, maxFiles - (existingFiles?.length || 0)));
      }
      return merged;
    });

    setError("");
  };

  const handleFileChange = (e) => {
    validateAndAddFiles(e.target.files);
    e.target.value = "";
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const pastedImages = [];
    for (const item of items) {
      if (item.type?.startsWith("image/")) {
        const blob = item.getAsFile();
        if (blob) {
          const ext = blob.type.split("/")[1] || "png";
          pastedImages.push(
            new File([blob], `pasted-screenshot-${Date.now()}.${ext}`, { type: blob.type })
          );
        }
      }
    }

    if (pastedImages.length > 0) {
      validateAndAddFiles(pastedImages);
      e.preventDefault();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    validateAndAddFiles(e.dataTransfer.files);
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setError("");
  };

  const removeExisting = async (file) => {
    try {
      await onRemoveExisting(file);
      setError("");
    } catch (err) {
      setError("Failed to remove existing file. Please try again.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const raw = (value ?? "").toString().trim();
    const n = Number(raw);

    if (!raw || !Number.isInteger(n) || n <= 0) {
      setError(`${label} must be a positive integer.`);
      return;
    }

    const totalEvidenceCount = (existingFiles?.length || 0) + (files?.length || 0);
    if (requireFile && totalEvidenceCount < 1) {
      setError("Please upload at least 1 evidence file (or paste a screenshot).");
      return;
    }

    // ✅ Submit number + ONLY NEW files (existing ones already stored)
    onSubmit({ value: raw, files });
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} onPaste={handlePaste}>
        <div className="mb-3">
          <label className="form-label" htmlFor="numberInputModalField">
            {label}
          </label>
          <input
            id="numberInputModalField"
            type="number"
            className={`form-control ${error ? "is-invalid" : ""}`}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError("");
            }}
            placeholder={placeholder}
            min={1}
            step={1}
            autoFocus
          />
          {error && <div className="invalid-feedback">{error}</div>}
        </div>

        {/* ✅ Existing files */}
        {existingFiles?.length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <label className="form-label mb-1">Existing Evidence</label>
              <div className="small text-muted">{existingFiles.length} file(s)</div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              {existingFiles.map((f) => (
                <div key={f.fileId || f.name} className="border rounded p-2" style={{ width: 220 }}>
                  <div className="small text-truncate" title={f.name}>
                    {f.name}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger w-100 mt-2"
                    onClick={() => removeExisting(f)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New uploads */}
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <label className="form-label mb-1">
              Evidence Attachments {requireFile ? <span className="text-danger">*</span> : null}
            </label>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Browse files
            </button>
          </div>

          <div
            className="border rounded p-3"
            style={{ cursor: "pointer" }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
          >
            <div className="small text-muted">
              Drag & drop files here, or click to browse. You can also paste a screenshot with <b>Ctrl+V</b>.
            </div>

            <div className="small text-warning mt-1">
              ⚠ Only the following file types are allowed:{" "}
              <span className="fw-semibold">
                {accept.replaceAll(",", ", ")}
              </span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              accept={accept}
              multiple={allowMultiple}
              onChange={handleFileChange}
            />
          </div>

          {files.length > 0 && (
            <div className="mt-3">
              <div className="small text-muted mb-2">
                New uploads ({files.length}/{Math.max(0, maxFiles - (existingFiles?.length || 0))})
              </div>

              <div className="d-flex flex-wrap gap-2">
                {previews.map((p, idx) => (
                  <div key={idx} className="border rounded p-2" style={{ width: 160 }}>
                    {p.url ? (
                      <img
                        src={p.url}
                        alt={p.name}
                        style={{ width: "100%", height: 90, objectFit: "cover" }}
                        className="rounded"
                      />
                    ) : (
                      <div
                        className="bg-light rounded d-flex align-items-center justify-content-center"
                        style={{ width: "100%", height: 90 }}
                      >
                        <span className="small text-muted">No preview</span>
                      </div>
                    )}

                    <div className="mt-2 small text-truncate" title={p.name}>
                      {p.name}
                    </div>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger w-100 mt-2"
                      onClick={() => removeFileAt(idx)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {submitText}
          </button>
        </div>
      </form>
    </Modal>
  );
}
